import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FiShoppingBag } from "react-icons/fi";
import useMerchantOrders from "../../../Routes/Marketplace/libs/useMerchantOrders";
import UserDataFetcher from "../../../libs/getUserbyUserId";
import PageHeader from "../../components/PageHeader";
import FilterBar from "../../components/FilterBar";
import DataTable from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import { getMerchantOrderTotal } from "../utils/orderHelpers";
import OrderPaymentBadge from "../../../Routes/Marketplace/components/OrderPaymentBadge";

const STATUS_OPTIONS = ["Pending", "Processing", "Shipped", "Delivered", "Canceled"];

const MerchantOrdersPage = () => {
  const { data: orders = [], isLoading, isError } = useMerchantOrders();
  const { userData } = UserDataFetcher();
  const merchantId = userData?.user?.userId || userData?.user?._id;
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return orders.filter((order) => {
      if (statusFilter !== "all" && order.status !== statusFilter) return false;
      if (!term) return true;
      const customer = `${order.user?.first_name || ""} ${order.user?.last_name || ""} ${order.shippingAddress?.full_name || ""}`.toLowerCase();
      return (
        String(order.orderNumber || "").toLowerCase().includes(term) ||
        customer.includes(term) ||
        (order.shippingAddress?.phone_number || "").includes(term) ||
        (order.shippingAddress?.email || "").toLowerCase().includes(term)
      );
    });
  }, [orders, statusFilter, search]);

  const columns = [
    { key: "orderNumber", label: "Order #" },
    { key: "date", label: "Date", render: (r) => new Date(r.createdAt).toLocaleDateString() },
    {
      key: "customer",
      label: "Customer",
      render: (r) => `${r.user?.first_name || ""} ${r.user?.last_name || ""}`.trim() || r.shippingAddress?.full_name || "Guest",
    },
    {
      key: "total",
      label: "Your total",
      render: (r) => `NGN ${getMerchantOrderTotal(r, merchantId).toLocaleString()}`,
    },
    {
      key: "payment",
      label: "Payment",
      render: (r) => <OrderPaymentBadge order={r} />,
    },
    { key: "status", label: "Order status", render: (r) => <StatusBadge status={r.status} /> },
    {
      key: "actions",
      label: "",
      render: (r) => (
        <Link to={`/merchant/orders/${r._id}`} className="dashboard-btn dashboard-btn--secondary">
          View
        </Link>
      ),
    },
  ];

  return (
    <>
      <PageHeader title="Orders" subtitle="Manage and fulfill customer orders" icon={FiShoppingBag} />
      <FilterBar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search order #, customer, phone…">
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
      </FilterBar>
      {isError ? (
        <p style={{ color: "var(--dash-danger)" }}>Failed to load orders.</p>
      ) : (
        <DataTable
          columns={columns}
          rows={filtered.map((o) => ({ ...o, _key: o._id }))}
          loading={isLoading}
          emptyMessage="No orders found"
        />
      )}
      {!isLoading && filtered.length === 0 && !isError && (
        <EmptyState icon={FiShoppingBag} title="No orders yet" message="Orders containing your products will appear here." />
      )}
    </>
  );
};

export default MerchantOrdersPage;
